import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { FormCard, FormGrid } from "../FormCard";
import { InputRow, SelectRow } from "../Rows";
import { useAuth } from "../../providers/AuthContext";
import { getAllBiddingZones } from "../../api/Resources";
import { getAllGridAreas } from "../../api/GridCost";
import { getGridVoltageSiteOptions } from "./gridCostConstants";
import { normalizeDecimalInput } from "./gridCostUtils";

export default function SiteGridSettings({ chargingSite, setChargingSite, setHasChanges }) {
    const { t } = useTranslation();
    const { getAccessToken } = useAuth();
    const [gridAreas, setGridAreas] = useState([]);
    const [loadingAreas, setLoadingAreas] = useState(true);
    const [biddingZones, setBiddingZones] = useState([]);
    const [loadingBiddingZones, setLoadingBiddingZones] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const loadGridAreas = async () => {
            try {
                setLoadingAreas(true);
                const token = await getAccessToken();
                const areas = (await getAllGridAreas(token)) || [];
                if (!cancelled) {
                    setGridAreas(
                        areas.slice().sort((a, b) =>
                            (a.gridAreaCode || "").localeCompare(b.gridAreaCode || "", undefined, {
                                sensitivity: "base",
                            })
                        )
                    );
                }
            } catch (err) {
                if (!cancelled) {
                    toast.error(err.message);
                }
            } finally {
                if (!cancelled) {
                    setLoadingAreas(false);
                }
            }
        };

        const loadBiddingZones = async () => {
            try {
                setLoadingBiddingZones(true);
                const token = await getAccessToken();
                const zones = (await getAllBiddingZones(token)) || [];
                if (!cancelled) {
                    setBiddingZones(
                        zones.slice().sort((a, b) =>
                            (a.biddingZoneName || "").localeCompare(b.biddingZoneName || "", undefined, {
                                sensitivity: "base",
                            })
                        )
                    );
                }
            } catch (err) {
                if (!cancelled) {
                    toast.error(err.message);
                }
            } finally {
                if (!cancelled) {
                    setLoadingBiddingZones(false);
                }
            }
        };

        loadGridAreas();
        loadBiddingZones();

        return () => {
            cancelled = true;
        };
    }, [getAccessToken]);

    const gridAreaOptions = [
        { value: "", label: loadingAreas ? t("common.loading") : t("gridCost.noGridArea") },
        ...gridAreas.map((a) => ({
            value: a.gridAreaPublicId,
            label: `${a.gridAreaCode} — ${a.areaName}`,
        })),
    ];

    const voltageOptions = getGridVoltageSiteOptions(t);

    const biddingZoneOptions = [
        { value: "", label: loadingBiddingZones ? t("common.loading") : t("gridCost.noBiddingZone") },
        ...biddingZones.map((z) => ({
            value: z.biddingZonePublicId,
            label: z.biddingZoneName,
        })),
    ];

    const onFieldChange = (field, value) => {
        setChargingSite((prev) => ({ ...prev, [field]: value }));
        setHasChanges?.(true);
    };

    return (
        <FormCard
            title={t("form.gridConnection")}
            description={t("gridCost.gridSettingsDescription")}
            className="mt-6"
        >
            <FormGrid>
                <SelectRow
                    label={t("form.gridArea")}
                    value={chargingSite?.gridAreaPublicId ?? ""}
                    options={gridAreaOptions}
                    onChange={(value) => onFieldChange("gridAreaPublicId", value)}
                />
                <SelectRow
                    label={t("form.voltageLevel")}
                    value={chargingSite?.gridVoltageLevel ?? ""}
                    options={voltageOptions}
                    isClearable={false}
                    onChange={(value) => onFieldChange("gridVoltageLevel", value ?? "")}
                />
                <SelectRow
                    label={t("form.biddingZone")}
                    value={chargingSite?.biddingZonePublicId ?? ""}
                    options={biddingZoneOptions}
                    onChange={(value) => onFieldChange("biddingZonePublicId", value)}
                />
                <InputRow
                    label={t("form.connectionKw")}
                    value={chargingSite?.connectionKw ?? ""}
                    onChange={(value) => onFieldChange("connectionKw", normalizeDecimalInput(value))}
                />
            </FormGrid>
        </FormCard>
    );
}
